import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { theme } from 'styles/theme';

/**
 * 그리드 찾기/바꾸기 대화상자
 * - initialScope: 열 시 기본 범위 (블록이 있으면 'selection')
 * - hasSelectionSnapshot: 열기 직전 블록이 저장되어 있으면 true
 */
const GridFindReplaceDialog = ({
  open,
  onClose,
  initialFind = '',
  currentField = '',
  columnOptions = [],
  allowReplace = true,
  initialScope = 'column',
  hasSelectionSnapshot = false,
  onFindNext,
  onReplace,
  onReplaceAll,
}) => {
  const [findText, setFindText] = useState(initialFind);
  const [replaceText, setReplaceText] = useState('');
  const [scope, setScope] = useState(initialScope);
  const [matchCase, setMatchCase] = useState(false);
  const [field, setField] = useState(currentField || '');
  const findRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setFindText(initialFind || '');
    setField(currentField || columnOptions[0]?.id || '');
    setScope(initialScope || 'column');
    setTimeout(() => findRef.current?.focus?.(), 50);
  }, [open, initialFind, currentField, columnOptions, initialScope]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
        return;
      }
      if (e.key === 'Enter' && !(e.target instanceof HTMLButtonElement)) {
        e.preventDefault();
        onFindNext?.({ findText, replaceText, scope, matchCase, field });
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose, onFindNext, findText, replaceText, scope, matchCase, field]);

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-label="찾기/바꾸기"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10060,
        background: 'rgba(15, 23, 42, 0.35)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: 80,
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        style={{
          width: 420,
          background: theme.colors.white,
          borderRadius: theme.borderRadius?.md || 8,
          border: `1px solid ${theme.colors.border}`,
          boxShadow: '0 16px 40px rgba(0,0,0,0.18)',
          padding: 16,
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div style={{ fontWeight: 700, marginBottom: 12, color: theme.colors.text.primary }}>찾기 / 바꾸기</div>
        <label style={labelStyle}>
          찾을 내용
          <input
            ref={findRef}
            value={findText}
            onChange={(e) => setFindText(e.target.value)}
            style={inputStyle}
          />
        </label>
        {allowReplace && (
          <label style={labelStyle}>
            바꿀 내용
            <input
              value={replaceText}
              onChange={(e) => setReplaceText(e.target.value)}
              style={inputStyle}
            />
          </label>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <label style={{ ...labelStyle, flex: 1 }}>
            범위
            <select value={scope} onChange={(e) => setScope(e.target.value)} style={inputStyle}>
              <option value="column">현재 열</option>
              <option value="all">전체 열</option>
              <option value="selection" disabled={!hasSelectionSnapshot}>
                선택 영역{hasSelectionSnapshot ? '' : ' (없음)'}
              </option>
            </select>
          </label>
          {scope === 'column' && (
            <label style={{ ...labelStyle, flex: 1 }}>
              열
              <select value={field} onChange={(e) => setField(e.target.value)} style={inputStyle}>
                {columnOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        {scope === 'selection' && hasSelectionSnapshot && (
          <div style={{ marginTop: 6, fontSize: theme.typography.fontSize.xs, color: theme.colors.text.tertiary }}>
            대화상자를 열기 직전에 지정한 블록을 사용합니다. (열어도 범위는 유지됩니다)
          </div>
        )}
        {scope === 'selection' && !hasSelectionSnapshot && (
          <div style={{ marginTop: 6, fontSize: theme.typography.fontSize.xs, color: theme.colors.status.warning }}>
            먼저 그리드에서 블록을 드래그한 뒤, Ctrl+H 또는 우클릭 → 찾기/바꾸기로 열어주세요.
          </div>
        )}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: theme.typography.fontSize.sm }}>
          <input type="checkbox" checked={matchCase} onChange={(e) => setMatchCase(e.target.checked)} />
          대소문자 구분
        </label>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 16 }}>
          <button type="button" onClick={onClose} style={btnSecondary}>
            닫기
          </button>
          <button
            type="button"
            onClick={() => onFindNext?.({ findText, replaceText, scope, matchCase, field })}
            style={btnSecondary}
          >
            다음 찾기
          </button>
          {allowReplace && (
            <>
              <button
                type="button"
                onClick={() => onReplace?.({ findText, replaceText, scope, matchCase, field })}
                style={btnSecondary}
              >
                바꾸기
              </button>
              <button
                type="button"
                onClick={() => onReplaceAll?.({ findText, replaceText, scope, matchCase, field })}
                style={btnPrimary}
              >
                모두 바꾸기
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

const labelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  marginTop: 8,
  fontSize: theme.typography.fontSize.sm,
  color: theme.colors.text.secondary,
};

const inputStyle = {
  padding: '6px 8px',
  border: `1px solid ${theme.colors.border}`,
  borderRadius: 4,
  fontSize: theme.typography.fontSize.sm,
  boxSizing: 'border-box',
  width: '100%',
};

const btnSecondary = {
  padding: '6px 12px',
  border: `1px solid ${theme.colors.border}`,
  borderRadius: 4,
  background: theme.colors.white,
  cursor: 'pointer',
  fontSize: theme.typography.fontSize.sm,
};

const btnPrimary = {
  ...btnSecondary,
  border: 'none',
  background: theme.colors.primary,
  color: theme.colors.white,
  fontWeight: 600,
};

export default GridFindReplaceDialog;
